import { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Snackbar, Alert } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { useDeleteProduct } from 'hooks/useProduct';
import ConfirmDeleteDialog from 'ui-component/ConfirmDeleteDialog';
import PropTypes from 'prop-types';

export default function ProductActions({ productId, productName }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open____ = Boolean(anchorEl);
    const navigate = useNavigate();

    // UI States
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Initialize the delete mutation
    const { mutate: deleteProduct, isPending: isDeleting } = useDeleteProduct();

    const handleOpenDialog = () => {
        setAnchorEl(null);
        setConfirmDeleteOpen(true);
    };

    const handleConfirmDelete = () => {
        deleteProduct(productId, {
            onSuccess: () => {
                setConfirmDeleteOpen(false);
                setSnackbar({
                    open: true,
                    message: 'Product deleted successfully!',
                    severity: 'success'
                });

                // Wait slightly for the user to see the toast, then navigate
                setTimeout(() => {
                    navigate('/vendor/products');
                }, 1500);
            },
            onError: () => {
                setConfirmDeleteOpen(false);
                setSnackbar({
                    open: true,
                    message: 'Failed to delete product. Please try again.',
                    severity: 'error'
                });
            }
        });
    };

    return (
        <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} disabled={isDeleting}>
                {isDeleting ? <CircularProgress size={20} /> : <MoreVertIcon />}
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open____}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => navigate(`/vendor/products/${productId}`)}>
                    <ListItemIcon>
                        <VisibilityIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => navigate(`/vendor/products/${productId}/edit`)}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>

                <MenuItem onClick={handleOpenDialog} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            {/* Confirmation Dialog */}
            <ConfirmDeleteDialog
                open={confirmDeleteOpen}
                loading={isDeleting}
                title="Delete Product?"
                subtitle={`Are you sure you want to delete "${productName || 'this item'}"? This action cannot be undone.`}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
            />

            {/* Success/Error Toast */}
            {/* <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="contained" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar> */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

ProductActions.propTypes = {
    productId: PropTypes.string.isRequired,
    productName: PropTypes.string
};
